/* eslint-disable no-nested-ternary */
import React, { Fragment, useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment-timezone';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import SettingsIcon from '@material-ui/icons/Settings';
import { IconButton } from '@material-ui/core';
import {
  InProgress,
  Submitted,
  Edit,
  Resolve,
  Delete,
  ALL,
} from '@mummssoftware/utils/constants';
import { PNCounterIcon } from '@mummssoftware/global-context/PNCounter';
import { GlobalContext } from '@mummssoftware/global-context/GlobalContext';
import {
  ENTITY_PATIENT_CARE_PLAN_PROBLEM_V2,
  ENTITY_PATIENT_CARE_PLAN_PROBLEM_TASK,
} from '@mummssoftware/global-context/PNCounter/constants';
import {
  getFirstUserCredential,
  getLastUserCredential,
  getCPPDate,
} from '@mummssoftware/mumms-print/PDFComponents/CarePlanProblems/utils';
import {
  formatDateMMDDYY,
  formatDateYYYYMMDD,
  YYYYMMDDtoMMDDYY,
} from '@mummssoftware/utils/formatters';
import ConfirmModal from './ConfirmModal';
import { PrintSingleCPP } from '../Print';
import { CPPContext } from '../Context/CPPContext';
import { hasPlanTasks } from '../Context/utils';
import { Context } from '../../context';
import {
  SET_CPP,
  SET_ERROR,
  Editing,
  noDeleteOptions,
  editDeleteOptions,
  allOptions,
  noOptions,
  deletePrintOptions,
  printOptions,
  Print,
  cppMenu,
  cppResolve,
  EditFullMode,
} from '../Context/constants';
import {
  CellDiv,
  StyledSpan,
  DateInfoDiv,
} from '../../components/layout/WrapperDiv';
import defaults from '../../translations/en.json';

export const EffPlanTableCellRenderer = React.memo((props: any) => {
  const { data } = props && props;
  const inProgress = data.status === InProgress;
  const text = inProgress
    ? InProgress
    : YYYYMMDDtoMMDDYY(data.effectivedate) ||
      (data.modifieddatetime && formatDateMMDDYY(data.modifieddatetime)) ||
      (data.createdatetime && formatDateMMDDYY(data.createdatetime));
  return (
    <CellDiv>
      <StyledSpan inProgress={inProgress}>{text}</StyledSpan>
    </CellDiv>
  );
});

type PlanHeaderProps = {
  patientCPP: mumms.patientCarePlanProblem;
};

// Resolved, Submitted or In Progress
export const StatusCell = React.memo(({ patientCPP }: PlanHeaderProps) => {
  const {
    createpersonfirstname,
    createpersonlastname,
    modifiedpersonfirstname,
    modifiedpersonlastname,
    status,
  } = patientCPP;
  const inProgress = status === InProgress;
  const submitted = status === Submitted;

  const modifiedName = modifiedpersonfirstname && modifiedpersonlastname;
  const credential = modifiedName
    ? getLastUserCredential(patientCPP)
    : getFirstUserCredential(patientCPP);
  const text = inProgress
    ? InProgress
    : submitted
    ? defaults.updated
    : defaults.resolved;
  const cellDate = getCPPDate(patientCPP);
  const userName = createpersonfirstname && createpersonlastname;
  const cellText = inProgress
    ? InProgress
    : userName // ADMIN users no name recording
    ? `${text}: ${cellDate} by ${credential}`
    : `${text}: ${cellDate}`;
  return <DateInfoDiv {...{ inProgress }}>{cellText}</DateInfoDiv>;
});

export const EffDateCell = React.memo(({ patientCPP }: PlanHeaderProps) => {
  const {
    createpersonfirstname,
    createpersonlastname,
    modifiedpersonfirstname,
    modifiedpersonlastname,
    effectivedate,
    modifieddate,
    status,
  } = patientCPP;
  const inProgress = status === InProgress;
  // very bad solution to lack of effectivedatetime
  const createdCredential = getFirstUserCredential(patientCPP);
  let effectiveCellText = `${defaults.effective}: ${YYYYMMDDtoMMDDYY(
    effectivedate,
  )}`;
  const userName = createpersonfirstname && createpersonlastname;
  if (userName) {
    // this is not an admin
    effectiveCellText += ` by ${createdCredential}`;
  }

  const modifiedCredential = getLastUserCredential(patientCPP);
  let modifiedCellText = `${defaults.lastUpdated}: ${YYYYMMDDtoMMDDYY(
    modifieddate,
  )}`;
  const modifiedName = modifiedpersonfirstname && modifiedpersonlastname;
  if (modifiedName) {
    modifiedCellText += ` by ${modifiedCredential}`;
  }
  return (
    <div>
      <DateInfoDiv>{effectiveCellText}</DateInfoDiv>
      {inProgress && <DateInfoDiv>{modifiedCellText}</DateInfoDiv>}
    </div>
  );
});

export const EditCellRenderer = React.memo((props: any) => {
  const intl = useIntl();

  const {
    resolveDate,
    cppForm,
    getPatientCppTasks,
    canEditCPPRoleGroup,
    dispatch,
  } = useContext(CPPContext);
  const {
    resolveCarePlanProblem,
    clearSavedCarePlanProblems,
    deleteCarePlanProblem,
    currentUser,
    patient,
    agency,
    observation,
    readOnly,
  } = useContext(Context);
  const { patientDateRestrictions } = useContext(GlobalContext);
  const basicDateConstraint = patientDateRestrictions?.basicDateConstraint;

  const [anchorEl, setAnchorEl] = useState(null);
  const [modalOption, setModalOption] = useState<any>(null);

  const patientCPP = props.data.patientCPP || props.data;
  const createDate = new Date(patientCPP.createdatetime);
  // you can delete a problem whose creation date is above the basic constraints
  const canDeleteCPP =
    basicDateConstraint && moment(createDate) > moment(basicDateConstraint);
  const inProgress = patientCPP.status === InProgress;
  const isResolved = patientCPP.resolveddate;
  const resolvedNoDelete = isResolved && !canDeleteCPP;
  const resolvedDelete = isResolved && canDeleteCPP;
  // option to delete or not

  let cellOptions: string[] = noOptions;
  if (readOnly) {
    cellOptions = printOptions;
  } else if (canEditCPPRoleGroup) {
    cellOptions = resolvedNoDelete
      ? printOptions
      : resolvedDelete
      ? deletePrintOptions
      : inProgress
      ? editDeleteOptions
      : canDeleteCPP
      ? allOptions
      : noDeleteOptions;
  } else {
    cellOptions = inProgress ? noOptions : printOptions;
  }

  const onClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (event: any) => {
    // stop propagation of panel expansion
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleEditOption = (option: mumms.CPPType) => {
    clearSavedCarePlanProblems();
    getEditForm(patientCPP, option);
    setAnchorEl(null);
  };

  /**
   * Edit from gear on expanded care plan problems
   * - get CPP form definition
   * - get Tasks definition if needed
   */
  const getEditForm = async (cpp: any, option: mumms.CPPType) => {
    // reset error
    dispatch({
      type: SET_ERROR,
      payload: null,
    });
    // so that the loading message appears
    dispatch({
      type: SET_CPP,
      payload: {
        ...cpp,
        type: option,
      },
    });
    const mode: mumms.CPPMode = ALL;

    // if tasks get Patient CPP Tasks
    if (hasPlanTasks(cpp)) {
      await getPatientCppTasks(cpp);
    }

    await observation
      .getPatientCarePlanProblemByIdv2(cpp.id, mode, agency)
      .then(async (res: any) => {
        if (!(res instanceof Response)) {
          // res type mumms.patientCarePlanProblem
          const {
            careplanproblemdescription,
            careplanproblemshortname,
            careplanproblemname,
            effectivedate,
            createpersonid,
            id,
            status,
            numtasks,
            userprovideddisplayname,
          } = cpp;
          // take the latest saved version when clicking on edit gear
          const planSubmission =
            res.formsubmission || res.formsubmissionautosave;
          dispatch({
            type: SET_CPP,
            payload: {
              careplanproblemdescription,
              careplanproblemshortname,
              careplanproblemname,
              effectivedate,
              createpersonid,
              id,
              type: option,
              formdefinition: planSubmission,
              status,
              numtasks,
              userprovideddisplayname,
            },
          });
        } else {
          dispatch({
            type: SET_ERROR,
            payload: intl.formatMessage(defaults.errorMsg, {
              status: res.status,
              cpp: cpp.careplanproblemname,
            }),
          });
        }
      })
      .catch(() => {
        dispatch({
          type: SET_ERROR,
          payload: intl.formatMessage(defaults.errorMsg, {
            status: '',
            cpp: cpp.careplanproblemname,
          }),
        });
      });
  };

  const selectOption = (option: string) => {
    switch (option) {
      case EditFullMode:
      case Edit: {
        const arg = option === EditFullMode ? EditFullMode : Editing;
        return handleEditOption(arg);
      }
      case Resolve:
        return setModalOption(Resolve);
      case Print:
        return setAnchorEl(null);
      case Delete:
        return setModalOption(Delete);
      default:
        return null;
    }
  };

  const onClickDelete = () => {
    deleteCarePlanProblem(patientCPP, patient);
    setAnchorEl(null);
    setModalOption(null);
  };

  const onClickResolve = () => {
    const { userId } = currentUser;
    const resolvedPlan = {
      ...patientCPP,
      resolveddate: formatDateYYYYMMDD(resolveDate || Date.now()),
    };
    resolvedPlan.createpersonid = userId;
    resolvedPlan.modifiedpersonid = userId;
    resolveCarePlanProblem(resolvedPlan);
    setAnchorEl(null);
    setModalOption(null);
  };

  const onCancelClick = () => {
    setAnchorEl(null);
    setModalOption(null);
  };

  const isEditing = !!(cppForm && cppForm.type === Editing);

  const getMenuUI = () =>
    modalOption ? (
      <div>
        <ConfirmModal
          onOKClick={modalOption === Resolve ? onClickResolve : onClickDelete}
          onCancelClick={onCancelClick}
          patientCPP={patientCPP}
          type={modalOption}
        />
      </div>
    ) : (
      <div>
        {cellOptions.map((option: string) => {
          const editID = option !== Edit ? cppResolve : cppMenu;
          return option === Print ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div id={editID} key={option} onClick={() => selectOption(option)}>
              <PrintSingleCPP patientCarePlanProblem={patientCPP} />
            </div>
          ) : (
            <MenuItem
              key={option}
              onClick={() => selectOption(option)}
              // different ID so that the panel does not expand
              id={editID}
            >
              {option}
            </MenuItem>
          );
        })}
      </div>
    );
  // resolved plan with no delete option => no gear
  return (
    <CellDiv>
      {(cellOptions.length && (
        <IconButton onClick={handleEdit} size="small" disabled={isEditing}>
          <SettingsIcon />
        </IconButton>
      )) ||
        ''}
      <Fragment>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
          {getMenuUI()}
        </Menu>
      </Fragment>
    </CellDiv>
  );
});

export const NoteCountCellRenderer = (props: any) =>
  !props.data.resolveddate ? (
    /* eslint camelcase: ["error", {allow: ["role_group_name"]}] */
    <PNCounterIcon
      patientNumber={props?.data?.patientnumber}
      entityKey={props?.data?.id.toString()}
      entityName={props?.data?.careplanproblemname}
      entityType={ENTITY_PATIENT_CARE_PLAN_PROBLEM_V2}
    />
  ) : (
    <CellDiv />
  );
export const TaskCountCellRenderer = (props: any) =>
  props.data ? (
    /* eslint camelcase: ["error", {allow: ["role_group_name"]}] */
    <PNCounterIcon
      patientNumber={props?.data?.patientnumber}
      entityKey={props?.data?.entitykey}
      entityName={props?.data?.tasktext}
      entityType={ENTITY_PATIENT_CARE_PLAN_PROBLEM_TASK}
    />
  ) : (
    <CellDiv />
  );
